import { RemoveIcon } from '../../svg'
import { cn } from '../../utils'
import * as Dialog from '@radix-ui/react-dialog'
import { useRef } from 'react'
import { useOnClickOutside } from 'usehooks-ts'
import {
    isDropdownMenuOpen,
    shouldIgnoreDropdownOutsideInteraction,
} from '../Dropdown/dropdownOutsideGuard'
import './Modal.css'
import { getButtonStyle, getModalClass, getOverlayClass } from './Modal.styles'
import { ModalProps } from './Modal.types'

export const Modal = ({
    title,
    closeTrigger,
    onCloseModal,
    content,
    isOpen,
    btnIconClass,
    className,
    footerComponent,
    onOutsideClick,
    isDnD = false,
    contentClassNames,
    footerComponentClassName,
    description,
    modalTitle = true,
    variant = 'auto',
    onCloseAttempt,
}: ModalProps) => {
    const ref = useRef(null)

    const handleClose = () => {
        if (onCloseAttempt && !onCloseAttempt()) return
        onCloseModal()
    }

    useOnClickOutside(ref, (event) => {
        const target = event.target
        // Guard against react-select dropdown being open (portal renders outside modal DOM)
        if (document.querySelector('.custom-class__menu')) {
            return
        }
        if (
            target instanceof Element &&
            (target.closest('[data-radix-menu-content]') ||
                target.closest('[data-radix-dropdown-menu-content]') ||
                target.closest('[data-radix-dialog-content]') ||
                target.closest('[data-radix-select-content]') ||
                target.closest('[data-radix-popover-content]') ||
                // Radix Dialog/Popover render content with role="dialog" and
                // AlertDialog with role="alertdialog" — match any other open
                // Radix dialog rendered on top of this modal, but not this
                // modal itself (clicks inside `ref` never reach this handler).
                (target.closest('[role="dialog"][data-state="open"]') &&
                    target.closest('[role="dialog"][data-state="open"]') !==
                        ref.current) ||
                target.closest('[role="alertdialog"][data-state="open"]') ||
                target.closest('.custom-class__menu-portal') ||
                target.closest('.custom-class__menu') ||
                target.closest('[class*="menu-portal"]'))
        ) {
            return
        }
        if (isDropdownMenuOpen()) {
            return
        }
        if (shouldIgnoreDropdownOutsideInteraction()) {
            return
        }
        if (onCloseAttempt && !onCloseAttempt()) return
        onOutsideClick?.()
    })

    return (
        <Dialog.Root open={isOpen} modal={false}>
            <Dialog.Portal>
                {/* Render overlay manually: Radix's Dialog.Overlay returns null when modal={false}. */}
                <div
                    aria-hidden="true"
                    className={cn(getOverlayClass(isOpen))}
                    style={{ zIndex: 'var(--z-modal-overlay)' }}
                />
                <Dialog.Content
                    aria-describedby="modal"
                    ref={ref}
                    className={cn(
                        getModalClass(isOpen, isDnD, variant),
                        className,
                        'flex gap-4'
                    )}
                    style={{ zIndex: 'var(--z-modal)' }}
                    onEscapeKeyDown={(e) => {
                        if (onCloseAttempt && !onCloseAttempt()) {
                            e.preventDefault()
                        } else {
                            onCloseModal()
                        }
                    }}
                >
                    <Dialog.DialogDescription className="hidden">
                        Description
                    </Dialog.DialogDescription>
                    {title && (
                        <div className="flex flex-row items-center justify-between">
                            <Dialog.Title className="hidden">
                                {title}
                            </Dialog.Title>
                            {!isDnD && modalTitle && (
                                <Dialog.Title>{title}</Dialog.Title>
                            )}
                            {!isDnD && modalTitle && (
                                <Dialog.Close asChild>
                                    <button
                                        onClick={handleClose}
                                        className={cn(
                                            getButtonStyle(),
                                            btnIconClass
                                        )}
                                    >
                                        {closeTrigger ? (
                                            closeTrigger
                                        ) : (
                                            <RemoveIcon />
                                        )}
                                    </button>
                                </Dialog.Close>
                            )}
                        </div>
                    )}
                    {description && (
                        <div className="flex w-full items-center justify-center">
                            <Dialog.Description>
                                {description}
                            </Dialog.Description>
                        </div>
                    )}
                    <div
                        className={cn(
                            'flex min-h-0 flex-1 flex-col overflow-y-auto py-0.5',
                            isDnD && 'h-full',
                            contentClassNames
                        )}
                    >
                        {content}
                    </div>
                    {!isDnD && footerComponent && (
                        <div
                            className={cn(
                                'flex w-full shrink-0 flex-col gap-2 border-t border-border-light py-2 align-baseline sm:flex-row sm:items-end sm:justify-end sm:gap-3',
                                footerComponentClassName
                            )}
                        >
                            {footerComponent}
                        </div>
                    )}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    )
}
